import { proxies, ProxyInfoType } from '@/model/proxyTypes';
import Card from '../../ui/Card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '../../ui/Dialog';
import Flex from '../../ui/Flex';
import { Minus, Plus } from 'lucide-react';
import { trafficTiers } from '@fd/common/biz/traffic';
import DiscountBadge from '../../PurchasePage/DiscountBadge';
import { ChangeEvent, useMemo, useState } from 'react';
import { ProxyTypeName } from '@fd/common/biz/proxyType';
import { SbSubscription } from '@fd/supabase/types';
import { BYTES_IN_GB } from '@fd/common/biz/formatBytesToGb';
import { Button } from '../../ui/Button';
import { calculateMultiSubscriptionTotals } from '@fd/common/biz/pricing';
import { deepEqual } from '@fd/common/deepEquals';
import { createCheckout } from '@/payment/createCheckout';

export default function UpdateSubscriptionDialogue(props: {
  isOpen: boolean;
  close: () => void;
  subscription: SbSubscription;
}) {
  const { isOpen, close, subscription } = props;

  const initialTraffic = useMemo(() => {
    return {
      residential: subscription.residential_bytes / BYTES_IN_GB,
      mobile: subscription.mobile_bytes / BYTES_IN_GB,
      datacenter: subscription.datacenter_bytes / BYTES_IN_GB,
      isp: subscription.isp_bytes / BYTES_IN_GB,
    };
  }, [subscription]);

  const [traffic, setTraffic] = useState(initialTraffic);

  const updateTraffic = (proxyType: ProxyTypeName, quantity: number) => {
    setTraffic((prev) => ({
      ...prev,
      [proxyType]: quantity,
    }));
  };

  const totals = useMemo(() => {
    return calculateMultiSubscriptionTotals(traffic);
  }, [traffic]);

  const isConfirmDisabled = useMemo(() => {
    return deepEqual(traffic, initialTraffic);
  }, [traffic, initialTraffic]);

  const handleConfirm = async () => {
    await createCheckout({
      gateway: 'stripe',
      residentialGb: traffic.residential,
      mobileGb: traffic.mobile,
      datacenterGb: traffic.datacenter,
      recurrent: true,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="min-h-fitmax-h-[90svh]">
        <DialogTitle>Update your subscription</DialogTitle>
        <Flex.Col className="gap-3">
          <div className="text-sm text-secondary">
            To update your subscription, enter the desired amount of GB and
            click Confirm. After payment, a new subscription will be created,
            and any remaining traffic will be rolled over to the new billing
            period.
          </div>
          <Flex.Col className="gap-3 overflow-y-auto max-h-[60dvh]">
            {proxies.map((p) => (
              <UpdateSubscriptionItem
                key={p.type}
                proxyInfo={p}
                quantity={traffic[p.type]}
                setQuantity={(quantity) => updateTraffic(p.type, quantity)}
              />
            ))}
          </Flex.Col>
        </Flex.Col>
        <DialogFooter>
          <Flex.Row className="gap-3 justify-end items-center">
            <div>
              {totals.totalPriceText}
              <span className="text-sm text-secondary"> / month</span>
            </div>
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button disabled={isConfirmDisabled} onClick={handleConfirm}>
              Confirm
            </Button>
          </Flex.Row>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpdateSubscriptionItem(props: {
  proxyInfo: ProxyInfoType;
  quantity: number;
  setQuantity: (quantity: number) => void;
}) {
  const { proxyInfo, quantity, setQuantity } = props;
  const [inputValue, setInputValue] = useState(quantity.toString());

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow empty input for typing purposes
    if (value === '') {
      setInputValue('');
      return;
    }

    // Only allow numeric input
    if (!/^\d+$/.test(value)) {
      return;
    }

    setInputValue(value);
  };

  const handleInputBlur = () => {
    const numValue =
      inputValue === ''
        ? proxyInfo.minQuantity
        : Math.max(proxyInfo.minQuantity, Number.parseInt(inputValue, 10));
    setInputValue(numValue.toString());
    setQuantityRaw(numValue);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const setQuantityRaw = (quantity: number) => {
    const newQuantity = Math.max(0, quantity);
    setInputValue(newQuantity.toString());
    setQuantity(newQuantity);
  };

  return (
    <Card className="bg-primary-background">
      <Flex.Col className="gap-2.5">
        <Flex.Row className="gap-3">
          <div className="bg-proxy-icon-bg h-10 aspect-square rounded-lg flex items-center justify-center">
            {proxyInfo.icon}
          </div>
          <Flex.Col className="gap-1 w-full">
            <div className="font-medium">{proxyInfo.title}</div>
            <Flex.Row className="gap-2 justify-between w-full">
              <div className="text-sm text-secondary">
                Shared - Payment per GB
              </div>
              <div className="text-sm text-secondary">
                ${proxyInfo.price} / GB
              </div>
            </Flex.Row>
          </Flex.Col>
        </Flex.Row>
        <div className="flex border border-proxy-border-light rounded-md">
          <button
            className="px-3 py-2"
            onClick={() => {
              setQuantityRaw(quantity - 1);
            }}
          >
            <Minus className="h-5 w-5 text-secondary" />
          </button>
          <div className="flex-1 relative">
            <input
              id={`traffic-quantity-${proxyInfo.type}`}
              type="text"
              inputMode="numeric"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              className="w-full h-full text-center py-2 focus:outline-none focus:ring-1 focus:ring-proxy-primary"
              aria-label="Traffic quantity in GB"
            />
            <span className="hidden sm:block absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary">
              GB
            </span>
          </div>
          <button
            className="px-3 py-2 "
            onClick={() => {
              setQuantityRaw(quantity + 1);
            }}
          >
            <Plus className="h-5 w-5 text-secondary" />
          </button>
        </div>
        <Flex.Row className="gap-2 mt-0.5">
          {trafficTiers.map((tier) => (
            <DiscountBadge
              key={tier.amountGb}
              discount={tier.discountPct}
              quantity={tier.amountGb}
              onClick={setQuantityRaw}
            />
          ))}
        </Flex.Row>
      </Flex.Col>
    </Card>
  );
}
